import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { TdoUserConfigurationModel } from '../models/configuration/user/tdo-user-configuration.model';
import { environment } from '../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { TdoUserRoleConfigurationModel } from '../models/configuration/user/tdo-user-role-configuration.model';
import { TdoUserGroupConfigurationModel } from '../models/configuration/user/tdo-user-group-configuration.model';
import { TdoUserInGroupConfigurationModel } from '../models/configuration/user/tdo-user-in-group-configuration.model';
import { ContactDataModel } from '../models/person/contact-data.model';
import { AddressDataModel } from '../models/person/address-data.model';
import { ContactCommsDataModel } from '../models/person/contact-comms-data.model';

@Injectable({
  providedIn: 'root'
})

export class UserService {
  public static authToken: string;
  private allUsers: TdoUserConfigurationModel[];
  private allUsersTDO: TdoUserConfigurationModel[];
  private currentTDOCid: string;

  public loggedInUser: TdoUserConfigurationModel;

  /**
   * Anywhere a bearer token is required, you can use this getter.  HttpClient already sets Content-Type to application/json by default.
   */
  static get authorizationHeaderObject(): object {
    return { headers: new HttpHeaders({ Authorization: `bearer ${UserService.authToken}` }) };
  }

  constructor(private http: HttpClient) {
    if (UserService.authToken === undefined) {
      UserService.authToken = localStorage.getItem('TDOauthToken');
    }
  }

  checkUsernameAvailable(userName: string) {
    return this.http.get<boolean>(`api/user-configuration/${userName}/checkavailable`);
  }
  getClients(): Observable<any[]> {
    const method: string = 'user-configuration/TDOClients';
    const url = environment.TDOurl + method;
    return this.http.get<any>(url);
  }

  getUser(userId: string) {
    if (userId === undefined || userId === null) {
      return of(null);
    }
    return this.http.get<TdoUserConfigurationModel>(`api/user-configuration/TDOUserById/${userId}`);
  }

  getUsers(tdocid: string): Observable<TdoUserConfigurationModel[]> {
    if (this.currentTDOCid === tdocid) {
      return of(this.allUsers);
    }
    else {
      this.currentTDOCid = tdocid;
      const method: string = 'user-configuration/tdo-users';
      const url = environment.TDOurl + method + '/' + tdocid;
      return this.http.get<TdoUserConfigurationModel[]>(url)
        .pipe(
          tap(_ => {
            this.allUsers = _;
          })
        );
    }
  }

  getAllUsers(tdocid: string): Observable<TdoUserConfigurationModel[]> {
    if (this.currentTDOCid === tdocid && this.allUsersTDO) {
      return of(this.allUsersTDO);
    }
    else {
      this.currentTDOCid = tdocid;
      const method: string = 'user-configuration/tdo-users-all';
      const url = environment.TDOurl + method + '/' + tdocid;
      return this.http.get<TdoUserConfigurationModel[]>(url)
        .pipe(
          tap(_ => {
            this.allUsersTDO = _;
          })
        );
    }
  }

  getAssistantUsers(tdocid: string): Observable<TdoUserConfigurationModel[]> {
    const method: string = 'user-configuration/get-assistant-users';
    const url = environment.TDOurl + method + '/' + tdocid;
    return this.http.get<TdoUserConfigurationModel[]>(url);
  }


  getProviderDoctorUsers(): Observable<TdoUserConfigurationModel[]> {
    const method: string = 'user-configuration/get-provider-doctor-users';
    const url = environment.TDOurl + method;
    return this.http.get<TdoUserConfigurationModel[]>(url);
  }

  getFormattedLabelUser(): Observable<TdoUserConfigurationModel[]> {
    const method: string = 'user-configuration/get-formatted-Label-user';
    const url = environment.TDOurl + method;
    return this.http.get<TdoUserConfigurationModel[]>(url);
  }


  saveUser(isNew: boolean, user: any): Observable<any> {
    let method: string;
    let url: string;
    if (isNew) {
      method = 'UserConfiguration/TDONewUserCreate';
      url = environment.TDOurl + method;
      return this.http.post<any>(url, user);
    } else {
      method = 'UserConfiguration/TDOUserUpdate';
      url = environment.TDOurl + method + '/' + user.UserID;
      return this.http.put<any>(url, user);
    }
  }

  resetCredentials(user: any): Observable<any> {

    const payload = {
      Username: user.UserName,
      FullName: user.FirstName + " " + user.LastName,
      UserId: user.UserID,
      ContactId: user.ContactId,
      LoginUrl: user.LoginUrl,
      BaseUrl: location.origin
    }
    return this.http.post<any>('api/user-configuration/resetCredentials', payload);
  }

  deleteUser(user: TdoUserConfigurationModel) {
    return this.http.post('api/user-configuration/delete-user', user);
  }

  getUserRoles(): Observable<TdoUserRoleConfigurationModel[]> {
    return this.http.get<TdoUserRoleConfigurationModel[]>('api/user-configuration/get-roles');
  }

  getUserRolesForUser(UserID: string): Observable<TdoUserRoleConfigurationModel[]> {
    return this.http.get<TdoUserRoleConfigurationModel[]>(`api/user-configuration/get-roles-for-user/${UserID}`);
  }



  deleteRole(roleId: string): Observable<void> {
    return this.http.get<void>(`api/user-configuration/delete-role/${roleId}`);
  }

  saveRoles(models: TdoUserRoleConfigurationModel[]): Observable<TdoUserRoleConfigurationModel[]> {
    return this.http.post<TdoUserRoleConfigurationModel[]>(`api/user-configuration/save-roles`, models);
  }

  getUserGroups(TDOCID: number): Observable<TdoUserGroupConfigurationModel[]> {
    return this.http.get<TdoUserGroupConfigurationModel[]>(`api/user-configuration/get-user-groups/${TDOCID}`);
  }

  saveUserGroups(groups: TdoUserGroupConfigurationModel[]): Observable<TdoUserGroupConfigurationModel[]> {
    return this.http.post<TdoUserGroupConfigurationModel[]>(`api/user-configuration/save-user-groups`, groups);
  }

  deleteUserGroup(groupId: string): Observable<void> {
    return this.http.get<void>(`api/user-configuration/delete-user-group/${groupId}`);
  }

  getUserInGroups(userId: string): Observable<TdoUserInGroupConfigurationModel[]> {
    return this.http.get<TdoUserInGroupConfigurationModel[]>(`api/user-configuration/get-user-in-groups/${userId}`);
  }

  saveUserInGroups(groups: TdoUserInGroupConfigurationModel[]): Observable<TdoUserInGroupConfigurationModel[]> {
    return this.http.post<TdoUserInGroupConfigurationModel[]>(`api/user-configuration/save-user-in-groups`, groups);
  }

  deleteUserInGroups(groups: TdoUserInGroupConfigurationModel[]): Observable<void> {
    return this.http.post<void>(`api/user-configuration/delete-user-in-groups`, groups);
  }

  getUserContact(contactId: string): Observable<ContactDataModel> {
    return this.http.get<ContactDataModel>(`api/user-configuration/get-user-contact/${contactId}`);
  }

  saveUserContact(model: ContactDataModel): Observable<ContactDataModel> {
    return this.http.post<ContactDataModel>(`api/user-configuration/save-user-contact`, model);
  }

  deleteContactAddress(model: AddressDataModel): Observable<void> {
    return this.http.get<void>(`api/user-configuration/delete-contact-address/${model.AddressId}`);
  }

  deleteContactCommunication(model: ContactCommsDataModel): Observable<void> {
    return this.http.get<void>(`api/user-configuration/delete-contact-communication/${model.CommMethID}`);
  }

  // Getters
  get users(): TdoUserConfigurationModel[] {
    return this.allUsers;
  }
}
